<script lang="ts" setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useRoute, useRouter, RouteLocationRaw } from 'vue-router';
import { useQuery } from '@vue/apollo-composable';
import { bucketUserRole } from '@/utils/bucket-utils';
import { deepParseDates } from '@/utils/date-utils';
import { BucketDocument, BucketType } from '@/gql/graphql';
import DataBrowser from '@/components/data/DataBrowser.vue';
import { useUserStore } from '@/store/user';
import { SHARING_USER_ROLES, BUCKET_DATA_TYPES } from '@/constants';
import { BAlert, BButton, BSpinner } from 'bootstrap-vue-next';
import { openRouterLink } from '@/utils/router-utils';

// Get the current route and router.
const route = useRoute();
const router = useRouter();

const pathPrefix = ref('');
const userStore = useUserStore();

// --- Bucket ID and Prefix from route ---
// Compute the numeric projectId by converting the route param to a number
const bucketId = computed(() => {
  const id = route.params.id as string;
  const parsedId = Number(id);
  if (isNaN(parsedId)) {
    throw new Error('Invalid bucket ID');
  }
  return parsedId;
});

// update the URL's 'path' parameter when changed
watch(pathPrefix, (newPath) => {
  router.replace({
    query: { ...route.query, path: newPath },
  });
});

// set the current folder path if 'path' present
onMounted(() => {
  if (route.query.path) {
    pathPrefix.value = String(route.query.path);
  }
});

// Query helper to parse boolean query parameters with a default
const qBool = (key: string, def = false) => {
  const v = route.query[key];
  if (v == null) return def;
  const s = Array.isArray(v) ? v[0] : v;
  return s === '1' || s === 'true';
};

// --- Go Back Link ---
const backLinkTarget = computed<RouteLocationRaw>(() => {
  const q = route.query;
  if (q.returnTo) {
    const path = decodeURIComponent(String(q.returnTo));
    return { path }; // robust even if it has its own query/hash
  }
  // sensible fallback if user loaded the page directly
  return { name: 'DataManager' };
});

const backLinkLabel = computed(() => {
  const q = route.query;
  if (q.returnLabel) {
    return `Back to ${String(q.returnLabel)}`;
  }
  // sensible fallback if user loaded the page directly
  return 'Go Back';
});

// props from query with sensible defaults
const showMetadata = computed(() => qBool('showMetadata', true));
const allowDownload = computed(() => qBool('allowDownload', true));
const listComputeTasks = computed(() => qBool('listComputeTasks', true));
const listSamples = computed(() => qBool('listSamples', true));
const listProjects = computed(() => qBool('listProjects', true));
const listUsers = computed(() => qBool('listUsers', true));

// --- Query Bucket ---
const {
  result: bucketQueryResult,
  loading: bucketQueryInProgress,
  error: bucketQueryError,
} = useQuery(BucketDocument, { id: bucketId.value });

// --- Buckets ---
const bucket = computed<BucketType | null>(() => {
  if (!bucketQueryResult.value?.bucket) {
    return null;
  }
  const bucketData = bucketQueryResult.value.bucket;
  return deepParseDates(bucketData) as BucketType;
});

const userRole = computed(() => {
  if (!bucket.value) {
    return SHARING_USER_ROLES.VIEWER;
  } else if (bucket.value.dataType === BUCKET_DATA_TYPES.SYSTEM) {
    return SHARING_USER_ROLES.EDITOR;
  }
  const role = bucketUserRole(userStore.id, bucket.value);
  if (role === null) {
    return SHARING_USER_ROLES.VIEWER;
  }
  return role;
});

// --- handlers of the UI components ---
function onBucketDeleted() {
  router.push(backLinkTarget.value);
}
</script>

<template>
  <div>
    <!-- Go Back Link -->
    <b-button
      @click="openRouterLink(backLinkTarget, $router, $event)"
      variant="outline-dark"
      class="mb-3"
      size="sm"
    >
      <font-awesome-icon icon="angle-left" class="me-2" />
      {{ backLinkLabel }}
    </b-button>

    <!-- Include the Data Browser component -->
    <div v-if="bucketQueryInProgress" class="loading-spinner">
      Loading... <b-spinner small />
    </div>
    <b-alert v-model="bucketQueryError" variant="danger">
      Error loading bucket: {{ bucketQueryError?.message }}
    </b-alert>
    <div v-if="!bucketQueryInProgress && !bucketQueryError && bucket">
      <DataBrowser
        :bucket-id="bucketId"
        :show-metadata="showMetadata"
        :role="userRole"
        :allow-download="allowDownload"
        :list-compute-tasks="listComputeTasks"
        :list-samples="listSamples"
        :list-projects="listProjects"
        :list-users="listUsers"
        v-model:pathPrefix="pathPrefix"
        @bucket-deleted="onBucketDeleted"
      />
    </div>
  </div>
</template>

<style scoped></style>
